/**
 * OverrideDrawer — supervisor-authority override UI (per mockup footer "✓ Override Authority").
 * Writes to PLANNED /v3/metrics/overrides. Today shipments: drawer collects the
 * reason + scope, dispatches a SessionEvent via /v3/staff/send-note (fire-and-forget),
 * and TODO-stubs the POST.
 */

import { useState } from 'react';
import { Banner, Button, ModalShell } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';

export interface OverrideDrawerProps {
  open: boolean;
  onClose: () => void;
}

type OverrideType = 'feeWaiver' | 'penaltyWaiver' | 'cipException' | 'limitOverride';

export function OverrideDrawer({ open, onClose }: OverrideDrawerProps): JSX.Element | null {
  const api = useApiClient();
  const { user } = useAuth();
  const [type, setType] = useState<OverrideType>('feeWaiver');
  const [customer, setCustomer] = useState<string>('');
  const [cartId, setCartId] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [status, setStatus] = useState<string | null>(null);

  async function submit(): Promise<void> {
    if (!customer || !reason) {
      setStatus('Customer and reason are required.');
      return;
    }
    setSubmitting(true);
    setStatus(null);
    try {
      // TODO Wave 3: replace with api.retail.metrics.postOverride({ ... }).
      // For now log the override as a staff note so there's an audit breadcrumb.
      if (user?.upn) {
        await api.retail.staff.sendNote({
          fromEmail: user.upn,
          fromName: user.displayName,
          toEmail: user.upn, // TODO: route to #compliance channel upn when known
          toName: user.displayName,
          messageBody: `[OVERRIDE-STUB] type=${type} customer=${customer} amount=${amount || 'n/a'} reason="${reason}"`,
          ...(cartId ? { cartId } : {}),
          contextLabel: 'RPM-Override',
        });
      }
      setStatus('Override recorded (audit breadcrumb sent; live endpoint pending).');
      setTimeout(() => { onClose(); setStatus(null); setCustomer(''); setCartId(''); setAmount(''); setReason(''); }, 1500);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Failed to submit override.');
    } finally {
      setSubmitting(false);
    }
  }

  const okMessage = status?.startsWith('Override') ?? false;

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Override authority"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit override'}
          </Button>
        </>
      }
    >
      <p style={{ fontSize: 13, color: 'var(--ink-600)', margin: 0 }}>
        Overrides post immediately with a full audit trail. Every override retains for 7 years.
      </p>

      <div style={{ display: 'grid', gap: 'var(--sp-6)' }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--pcb-blue-dark)' }}>
          Override type
          <select
            value={type}
            onChange={(e) => setType(e.target.value as OverrideType)}
            style={{ width: '100%', padding: 'var(--sp-5)', border: '1px solid var(--surface-border)', borderRadius: 'var(--r-md)', marginTop: 4, fontFamily: 'inherit' }}
          >
            <option value="feeWaiver">Fee waiver</option>
            <option value="penaltyWaiver">CD penalty waiver</option>
            <option value="cipException">CIP exception</option>
            <option value="limitOverride">Limit override</option>
          </select>
        </label>

        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--pcb-blue-dark)' }}>
          Customer name
          <input
            type="text"
            value={customer}
            onChange={(e) => setCustomer(e.target.value)}
            placeholder="e.g. John Smith"
            style={{ width: '100%', padding: 'var(--sp-5)', border: '1px solid var(--surface-border)', borderRadius: 'var(--r-md)', marginTop: 4, fontFamily: 'inherit' }}
          />
        </label>

        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--pcb-blue-dark)' }}>
          Cart ID (optional)
          <input
            type="text"
            value={cartId}
            onChange={(e) => setCartId(e.target.value)}
            placeholder="A-2026-0147"
            style={{ width: '100%', padding: 'var(--sp-5)', border: '1px solid var(--surface-border)', borderRadius: 'var(--r-md)', marginTop: 4, fontFamily: 'inherit' }}
          />
        </label>

        {(type === 'feeWaiver' || type === 'penaltyWaiver' || type === 'limitOverride') && (
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--pcb-blue-dark)' }}>
            Amount ($)
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              step={0.01}
              style={{ width: '100%', padding: 'var(--sp-5)', border: '1px solid var(--surface-border)', borderRadius: 'var(--r-md)', marginTop: 4, fontFamily: 'inherit' }}
            />
          </label>
        )}

        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--pcb-blue-dark)' }}>
          Reason (required)
          <textarea
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Death · goodwill · long-standing customer"
            style={{ width: '100%', padding: 'var(--sp-5)', border: '1px solid var(--surface-border)', borderRadius: 'var(--r-md)', marginTop: 4, fontFamily: 'inherit' }}
          />
        </label>
      </div>

      {status && <Banner tone={okMessage ? 'ok' : 'err'}>{status}</Banner>}
    </ModalShell>
  );
}
